import { ART_PAL, fade, mix, n, rng, shade, tint, vgrad, warm, type ArtTheme } from '../covers/kit';
import { defineScene, type SceneVariant } from './types';

/**
 * Dou Dizhu scene behind the game zone: a village courtyard tea house at dusk. At the top left the tiled eave of the
 * tea house (灰瓦, a lacquer-red eave board, upturned tip) with two red lanterns hanging from it; at the top right the
 * thatched straw roof of a farmhouse with one lantern — three lanterns, never four. A warm light pool over the table,
 * a calm middle, a few gold fireflies in the side bands. The phone keeps a smaller eave and straw roof with one
 * lantern each and the light. Nothing runs under the panels (no pillars, no floor line); roofs stay at the very top
 * corners, faint. No filters, no text. Motifs are marked `data-motif="<x>"` (tests keep them out of the middle 40%).
 */
export function doudizhuScene(theme: ArtTheme, v: SceneVariant): string {
  const P = ART_PAL[theme];
  const p = `dz${theme[0]}${v[0]}`;
  const wide = v === 'wide';
  const W = wide ? 1600 : 400;
  const H = wide ? 900 : 800;
  const k = wide ? 1 : 0.42;

  const wood = P.dark ? '#7A4E30' : '#9A6440';
  const top = P.dark ? mix(P.bg, P.purple, 0.1) : mix(P.orange, '#FFFFFF', 0.9);
  const mid = P.dark ? mix(P.bg, P.red, 0.08) : mix(P.gold, '#FFFFFF', 0.9);
  const bottom = P.dark ? mix(P.bg, wood, 0.16) : mix(P.red, '#FFFFFF', 0.92);
  const gold = warm(P);
  const tile = P.dark ? mix(P.slate, P.ink, 0.14) : mix(P.slate, '#FFFFFF', 0.58);
  const lacquer = P.dark ? shade(P.red, 0.2) : P.red;
  const straw = P.dark ? mix(P.gold, P.bg, 0.5) : mix(P.gold, '#FFFFFF', 0.45);
  const cord = P.dark ? mix(wood, P.bg, 0.2) : wood;
  const o = (dark: number, light: number) => n(P.dark ? dark : light);
  const X = (x: number) => n(x * k);
  const Y = (y: number) => n(y * k);

  // left eave: the roof plane above a curve from the page edge up to an upturned tip
  const eave = `M0 0H${X(330)}C${X(372)} ${Y(20)} ${X(410)} ${Y(38)} ${X(446)} ${Y(40)}C${X(360)} ${Y(92)} ${X(200)} ${Y(128)} 0 ${Y(150)}Z`;
  const eaveEdge = `M0 ${Y(150)}C${X(200)} ${Y(128)} ${X(360)} ${Y(92)} ${X(446)} ${Y(40)}`;
  // right straw roof: a soft thick thatch from the page edge
  const R = (x: number) => n(W - x * k);
  const thatch = `M${R(0)} 0H${R(420)}C${R(420)} ${Y(40)} ${R(380)} ${Y(70)} ${R(330)} ${Y(84)}C${R(230)} ${Y(112)} ${R(110)} ${Y(140)} ${R(0)} ${Y(164)}Z`;
  const thatchEdge = `M${R(330)} ${Y(84)}C${R(230)} ${Y(112)} ${R(110)} ${Y(140)} ${R(0)} ${Y(164)}`;

  const T = n(16 * k);
  const defs =
    vgrad(`${p}bg`, top, mid, bottom) +
    fade(`${p}pool`, gold, P.dark ? 0.13 : 0.2) +
    fade(`${p}calm`, P.dark ? P.bg : '#FFFFFF', P.dark ? 0.32 : 0.42) +
    fade(`${p}lh`, P.dark ? P.orange : P.gold, P.dark ? 0.4 : 0.3) +
    `<radialGradient id="${p}ln" cx=".4" cy=".35" r=".7"><stop offset="0" stop-color="${tint(P.red, 0.35)}"/><stop offset=".6" stop-color="${P.red}"/><stop offset="1" stop-color="${shade(P.red, 0.35)}"/></radialGradient>` +
    `<clipPath id="${p}ec"><path d="${eave}"/></clipPath>` +
    `<pattern id="${p}tl" width="${T}" height="${T}" patternUnits="userSpaceOnUse" patternTransform="rotate(-12)"><rect width="${n(10 * k)}" height="${T}" rx="${n(4 * k)}" fill="${shade(tile, 0.18)}"/></pattern>` +
    `<pattern id="${p}st" width="${n(9 * k)}" height="${n(14 * k)}" patternUnits="userSpaceOnUse" patternTransform="rotate(18)"><path d="M${n(2 * k)} 0V${n(10 * k)}" stroke="${shade(straw, 0.2)}" stroke-width="${n(1.4 * k + 0.4)}"/></pattern>`;

  /** a red lantern hanging from (x, y): cord, glow, a round body with ribs, gold caps and a gold tassel */
  const lantern = (x: number, y: number, r: number, drop: number) => {
    const cy = y + drop + r;
    return (
      `<g data-motif="${Math.round(x)}"><path d="M${n(x)} ${n(y)}V${n(cy - r)}" stroke="${cord}" stroke-width="${n(2 * k + 0.5)}"/>` +
      `<circle cx="${n(x)}" cy="${n(cy)}" r="${n(r * 2.6)}" fill="url(#${p}lh)"/>` +
      `<ellipse cx="${n(x)}" cy="${n(cy)}" rx="${n(r * 1.15)}" ry="${n(r)}" fill="url(#${p}ln)"/>` +
      `<path d="M${n(x)} ${n(cy - r)}V${n(cy + r)}M${n(x - r * 0.55)} ${n(cy - r * 0.86)}Q${n(x - r * 0.8)} ${n(cy)} ${n(x - r * 0.55)} ${n(cy + r * 0.86)}M${n(x + r * 0.55)} ${n(cy - r * 0.86)}Q${n(x + r * 0.8)} ${n(cy)} ${n(x + r * 0.55)} ${n(cy + r * 0.86)}" fill="none" stroke="${shade(P.red, 0.4)}" stroke-opacity=".5" stroke-width="${n(1.5 * k + 0.3)}"/>` +
      `<rect x="${n(x - r * 0.5)}" y="${n(cy - r - r * 0.2)}" width="${n(r)}" height="${n(r * 0.3)}" rx="${n(r * 0.08)}" fill="${gold}"/>` +
      `<rect x="${n(x - r * 0.5)}" y="${n(cy + r - r * 0.1)}" width="${n(r)}" height="${n(r * 0.3)}" rx="${n(r * 0.08)}" fill="${gold}"/>` +
      `<path d="M${n(x)} ${n(cy + r * 1.2)}V${n(cy + r * 2.4)}M${n(x - r * 0.18)} ${n(cy + r * 1.5)}V${n(cy + r * 2.2)}M${n(x + r * 0.18)} ${n(cy + r * 1.5)}V${n(cy + r * 2.2)}" stroke="${gold}" stroke-width="${n(2 * k + 0.4)}" stroke-linecap="round"/>` +
      `<ellipse cx="${n(x - r * 0.4)}" cy="${n(cy - r * 0.4)}" rx="${n(r * 0.3)}" ry="${n(r * 0.16)}" transform="rotate(-35 ${n(x - r * 0.4)} ${n(cy - r * 0.4)})" fill="#FFF" fill-opacity=".35"/></g>`
    );
  };

  // tile ends (瓦当) along the eave edge — an odd count
  let ends = '';
  const ec = wide ? 9 : 5;
  for (let i = 0; i < ec; i++) {
    const t = (i + 0.5) / ec;
    // cubic along the eave edge (0,150) → (446,40)
    const b = (a: number, c1: number, c2: number, d: number) =>
      (1 - t) ** 3 * a + 3 * (1 - t) ** 2 * t * c1 + 3 * (1 - t) * t * t * c2 + t ** 3 * d;
    ends += `<circle cx="${n(b(0, 200, 360, 446) * k)}" cy="${n(b(150, 128, 92, 40) * k + 3 * k)}" r="${n(6 * k)}"/>`;
  }

  const roofs =
    `<g data-motif="${Math.round(200 * k)}" opacity="${o(0.7, 0.62)}">` +
    `<path d="${eave}" fill="${tile}"/><rect width="${X(460)}" height="${Y(160)}" fill="url(#${p}tl)" clip-path="url(#${p}ec)"/>` +
    `<path d="${eaveEdge}" fill="none" stroke="${lacquer}" stroke-width="${n(9 * k + 1)}" stroke-linecap="round"/>` +
    `<path d="${eaveEdge}" fill="none" stroke="${gold}" stroke-opacity=".8" stroke-width="${n(1.6 * k + 0.4)}" transform="translate(0 ${n(6 * k + 1)})"/>` +
    `<g fill="${shade(tile, 0.1)}" stroke="${gold}" stroke-opacity=".6" stroke-width="${n(k + 0.3)}">${ends}</g></g>` +
    `<g data-motif="${Math.round(W - 210 * k)}" opacity="${o(0.62, 0.66)}">` +
    `<path d="${thatch}" fill="${straw}"/><path d="${thatch}" fill="url(#${p}st)"/>` +
    `<path d="${thatchEdge}" fill="none" stroke="${shade(straw, 0.28)}" stroke-width="${n(8 * k + 1)}" stroke-linecap="round" stroke-dasharray="${n(3 * k + 1)} ${n(5 * k + 1)}"/>` +
    `<path d="M${R(418)} ${Y(12)}C${R(300)} ${Y(38)} ${R(150)} ${Y(66)} ${R(0)} ${Y(92)}" fill="none" stroke="${shade(straw, 0.25)}" stroke-opacity=".7" stroke-width="${n(3 * k + 0.5)}"/></g>`;

  let art =
    `<rect width="${W}" height="${H}" fill="url(#${p}bg)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.1)}" rx="${n(W * 0.38)}" ry="${n(H * 0.5)}" fill="url(#${p}pool)"/>` +
    `<ellipse cx="${W / 2}" cy="${n(H * 0.5)}" rx="${n(W * 0.34)}" ry="${n(H * 0.42)}" fill="url(#${p}calm)"/>` +
    roofs;

  if (wide) {
    // two lanterns under the eave, one under the thatch; fireflies in the side bands
    art += lantern(150, 137, 26, 30) + lantern(340, 80, 22, 70) + lantern(1330, 113, 24, 44);
    const r = rng(33);
    const flies = (x0: number, count: number) => {
      let s = '';
      for (let i = 0; i < count; i++)
        s += `<circle cx="${n(x0 + r() * 340)}" cy="${n(380 + r() * 460)}" r="${n(1.6 + r() * 2)}"/>`;
      return `<g data-motif="${x0 + 170}" fill="${gold}" fill-opacity="${o(0.55, 0.6)}">${s}</g>`;
    };
    art += flies(40, 5) + flies(1220, 5);
  } else {
    art += lantern(46, 61, 12, 18) + lantern(W - 56, 64, 11, 22);
  }

  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" preserveAspectRatio="xMidYMid slice">` +
    `<defs>${defs}</defs>` +
    art +
    `</svg>`
  );
}

/** Header accent of Dou Dizhu: a row of roof-tile ends (瓦当) over a thin gold line (repeat-x tile, 16 × 8). */
export function doudizhuAccent(theme: ArtTheme): string {
  const P = ART_PAL[theme];
  const red = P.dark ? tint(P.red, 0.05) : P.red;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 8" width="16" height="8">` +
    `<path d="M0 7.5H16" stroke="${warm(P)}" stroke-opacity="${P.dark ? 0.7 : 0.85}"/>` +
    `<path d="M2 6A6 5 0 0 1 14 6" fill="none" stroke="${red}" stroke-opacity="${P.dark ? 0.6 : 0.75}" stroke-width="1.6"/>` +
    `</svg>`
  );
}

export default defineScene({ game: 'doudizhu', scene: doudizhuScene, accent: doudizhuAccent });
